import { styled } from '@mui/material';

import { extractTextMarking } from '../../helper/extract-text-markings';

export type CustomerRatingsProps = {
  headline: string;
  ratings: [RatingProps, RatingProps, RatingProps];
  className?: string;
};

export const CustomerRatings = ({
  headline,
  ratings,
  className,
}: CustomerRatingsProps) => (
  <div className={className}>
    <Headline>{headline}</Headline>
    <RatingsContainer>
      <RatingColumn {...ratings[0]} />
      <ColumnDivider />
      <RatingColumn {...ratings[1]} />
      <ColumnDivider />
      <RatingColumn {...ratings[2]} />
    </RatingsContainer>
  </div>
);

export type RatingProps = {
  largeText: string;
  smallText: string;
  description: string;
  footnoteIndex: string;
};

const RatingColumn = ({
  largeText,
  smallText,
  description,
  footnoteIndex,
}: RatingProps) => {
  const markedDescription = extractTextMarking(description);

  return (
    <RatingWrapper>
      <Rating>
        <span>{largeText}</span>
        {smallText}
      </Rating>
      <Description>
        {markedDescription.beforeMarking}
        <strong>{markedDescription.marking}</strong>
        {markedDescription.afterMarking}
        <sup>{footnoteIndex}</sup>
      </Description>
    </RatingWrapper>
  );
};

const Headline = styled('h2')(({ theme }) => ({
  ...theme.typography.h2,
  textAlign: 'center',
  width: '100%',
  margin: '0 0 var(--spacer-xl)',
}));

const RatingsContainer = styled('div')(({ theme }) => ({
  display: 'grid',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr auto 1fr auto 1fr',
  },

  [theme.breakpoints.only('xs')]: {
    rowGap: 'var(--spacer-xl)',
  },
}));

const ColumnDivider = styled('div')(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.up('sm')]: {
    display: 'block',
    margin: '0 var(--spacer-xl)',
    width: '2px',
    height: '100%',
    backgroundColor: 'var(--color-medium-light-grey)',
  },
}));

const RatingWrapper = styled('div')(({ theme }) => ({}));

const Rating = styled('h3')(({ theme }) => ({
  ...theme.typography.h2,
  '--largeTextSize': '90px',
  lineHeight: '1',
  color: 'var(--color-light-red)',
  margin: '0 0 var(--spacer-m)',

  span: {
    fontSize: 'var(--largeTextSize)',
  },

  [theme.breakpoints.up('sm')]: { '--largeTextSize': '100px' },

  [theme.breakpoints.up('md')]: { '--largeTextSize': '128px' },

  [theme.breakpoints.up('lg')]: { '--largeTextSize': '200px' },
}));

const Description = styled('p')(({ theme }) => ({
  ...theme.typography.h6,
  lineHeight: '23px',
  fontWeight: theme.typography.fontWeightRegular,
  textWrap: 'balance',
  margin: 0,

  strong: {
    ...theme.typography.h4,
    lineHeight: 'inherit',
  },

  sup: {
    ...theme.typography.footnote,
  },

  [theme.breakpoints.up('sm')]: { lineHeight: '27.3px' },
  [theme.breakpoints.up('md')]: { lineHeight: '35px' },
  [theme.breakpoints.up('lg')]: { lineHeight: '41px' },
}));
